import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Clock, ArrowLeft, ShoppingBag, Play } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProducerPage = () => {
  const { producerId } = useParams();
  const [producer, setProducer] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    fetchData();
  }, [producerId]);

  const fetchData = async () => {
    try {
      const [producerRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/producers/${producerId}`),
        axios.get(`${API_URL}/api/products`)
      ]);
      setProducer(producerRes.data);
      setProducts(productsRes.data.filter(p => p.producer_id === producerId));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`${product.name} sepete eklendi!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3625]"></div>
      </div>
    );
  }

  if (!producer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-[#1A3625] mb-4">Üretici bulunamadı</h2>
          <Link to="/market" className="btn-primary">
            Markete Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="producer-page">
      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[#E5DAC8]">
        <div className="container-custom">
          <Link to="/market" className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#1A3625] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Markete Dön
          </Link>
        </div>
      </div>

      {/* Producer Hero */}
      <section className="section-padding bg-[#1A3625]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="w-48 h-48 rounded-full overflow-hidden ring-8 ring-[#D4AF37]/20 mx-auto lg:mx-0">
                <img 
                  src={producer.image}
                  alt={producer.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="label-uppercase text-[#D4AF37] mb-4 block">{producer.title}</span>
              <h1 className="text-4xl md:text-5xl text-white font-semibold mb-4">{producer.name}</h1>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <span className="flex items-center gap-2 text-white/80">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  {producer.location}
                </span>
                <span className="flex items-center gap-2 text-white/80">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                  {producer.years_experience} yıl deneyim
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="max-w-xl"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="label-uppercase text-[#D4AF37] mb-4 block">Hikaye</span>
              <h2 className="text-3xl md:text-4xl text-[#1A3625] mb-6">Ben Kimim?</h2>
              <p className="text-lg text-[#5C5C5C] leading-relaxed">{producer.story}</p>
            </motion.div>
            
            {/* Video Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-video rounded-3xl overflow-hidden bg-[#1A3625] relative group cursor-pointer shadow-2xl"
                onClick={() => setShowVideo(true)}
              >
                <img 
                  src={producer.image}
                  alt={producer.name}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div 
                    className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Play className="w-8 h-8 text-[#1A3625] ml-1" fill="currentColor" />
                  </motion.div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <p className="text-base font-medium">{producer.name}'ın Hikayesi</p>
                  <p className="text-sm text-white/70">Tanıtım videosunu izleyin</p>
                </div>
              </div>
              
              {/* Video Modal */}
              {showVideo && (
                <motion.div 
                  className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onClick={() => setShowVideo(false)}
                >
                  <motion.div 
                    className="bg-white rounded-3xl p-4 max-w-3xl w-full"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="aspect-video bg-[#1A3625] rounded-2xl flex items-center justify-center">
                      <div className="text-center text-white">
                        <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">Video Yakında</p>
                        <p className="text-base text-white/70">Üretici tanıtım videoları hazırlanıyor</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowVideo(false)}
                      className="mt-4 w-full py-3 bg-[#1A3625] text-white rounded-full font-medium"
                    >
                      Kapat
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Ürünler</span>
            <h2 className="text-3xl md:text-4xl text-[#1A3625]">
              {producer.name}'ın Ürünleri
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                className="card-premium group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`product-${product.id}`}
              >
                <Link to={`/market/urun/${product.id}`}>
                  <div className="aspect-square rounded-2xl overflow-hidden mb-4">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </Link>
                
                <Link to={`/market/urun/${product.id}`}>
                  <h3 className="text-lg font-medium text-[#1A3625] mb-1 group-hover:text-[#D4AF37] transition-colors">
                    {product.name}
                  </h3>
                </Link>
                <p className="text-base text-[#5C5C5C] mb-3">{product.unit}</p>
                
                <div className="flex items-center justify-between">
                  <p className="text-xl font-semibold text-[#D4AF37]">₺{product.price}</p>
                  <button
                    onClick={() => handleAddToCart(product)}
                    className="w-10 h-10 bg-[#1A3625] text-white rounded-full flex items-center justify-center hover:bg-[#D4AF37] transition-colors"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingBag className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          
          {products.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#5C5C5C]">Bu üreticiye ait ürün bulunmuyor.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
